/**
 * MatchTermDrawer — Add or edit a match term.
 *
 * V1 match types and their field requirements:
 *   1: Create — pattern, event_category, event_type, ttl, case_sensitive
 *   2: Repeat — pattern, event_category, event_type, ttl, case_sensitive
 *   3: Clear  — pattern, event_category, event_type, ttl, case_sensitive
 *   4: Ignore — pattern, case_sensitive
 *   5: Change — pattern, replacement, case_sensitive
 *   6: Area   — pattern, area_id, case_sensitive
 *   7: Device — pattern, device_id (replacement = device_name), case_sensitive
 *
 * In ADD mode, the V1 uses "addMatchTypeId" which groups types:
 *   1 = Alarm Trigger (creates 1-3 CREATE/REPEAT/CLEAR terms together)
 *   2 = Change Text
 *   3 = Area Mapping
 *   4 = Device Mapping
 *   5 = Ignore
 *
 * In EDIT mode, we edit the individual match_type_id (1-7).
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

/** Add mode type options (V1 addMatchTypeId groups) */
const ADD_TYPE_OPTIONS = [
  { id: 1, label: 'Alarm Trigger (Create/Repeat/Clear)' },
  { id: 2, label: 'Change Text' },
  { id: 3, label: 'Area Mapping' },
  { id: 4, label: 'Device Mapping' },
  { id: 5, label: 'Ignore' },
];

/** Edit mode type labels (individual match_type_id) */
const EDIT_TYPE_LABELS = {
  1: 'Create', 2: 'Repeat', 3: 'Clear', 4: 'Ignore',
  5: 'Change', 6: 'Area', 7: 'Device',
};

const MatchTermDrawer = ({
  open, onClose, mode, item,
  onAdd, onEdit, saving,
  areas, virtualDevices, eventCategories, eventTypes,
  campusId, serverId, userId,
}) => {
  const isEdit = mode === 'edit';

  // Form state
  const [addTypeId, setAddTypeId] = useState(1);
  const [pattern, setPattern] = useState('');
  const [replacement, setReplacement] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [eventCatId, setEventCatId] = useState(0);
  const [eventTypeId, setEventTypeId] = useState(0);
  const [ttlMinutes, setTtlMinutes] = useState(0);
  const [areaId, setAreaId] = useState('');
  const [deviceId, setDeviceId] = useState('');

  // Populate form from item/props only when drawer opens (false → true)
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setAddTypeId(1);
      setPattern(item?.match_pattern || '');
      setReplacement(item?.replacement || '');
      setCaseSensitive(item?.is_case_sensitive === 1 || item?.is_case_sensitive === true);
      setEventCatId(item?.event_category_id || (eventCategories?.[0]?.event_category_id || 0));
      setEventTypeId(item?.event_type_id || (eventTypes?.[0]?.event_type_id || 0));
      setTtlMinutes(item?.ttl ? Math.round(item.ttl / 60) : 0);
      setAreaId(item?.area_id || '');
      setDeviceId(item?.device_id || '');
    }
    prevOpen.current = open;
  });

  // For add mode: use addTypeId to determine fields shown
  // For edit mode: use item.match_type_id
  const matchTypeId = isEdit ? (item?.match_type_id || 1) : null;

  // Determine which fields to show
  const showsPattern = true; // Always shown
  const showsAlarmFields = isEdit ? [1, 2, 3].includes(matchTypeId) : addTypeId === 1;
  const showsReplacement = isEdit ? matchTypeId === 5 : addTypeId === 2;
  const showsArea = isEdit ? matchTypeId === 6 : addTypeId === 3;
  const showsDevice = isEdit ? matchTypeId === 7 : addTypeId === 4;

  // Sort areas and devices for dropdowns
  const sortedAreas = useMemo(() =>
    [...(areas || [])].sort((a, b) => (a.area_name || '').localeCompare(b.area_name || '')),
    [areas]
  );

  const sortedDevices = useMemo(() =>
    [...(virtualDevices || [])].sort((a, b) => (a.device_name || '').localeCompare(b.device_name || '')),
    [virtualDevices]
  );

  const catOptions = useMemo(() =>
    [...(eventCategories || [])].sort((a, b) => (a.event_category_name || '').localeCompare(b.event_category_name || '')),
    [eventCategories]
  );

  const typeOptions = useMemo(() =>
    [...(eventTypes || [])].sort((a, b) => (a.event_type_name || '').localeCompare(b.event_type_name || '')),
    [eventTypes]
  );

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    if (isEdit) {
      // Build edit payload based on match_type_id
      const term = {
        matchterm_id: item.matchterm_id,
        pattern: pattern,
        is_case_sensitive: caseSensitive ? 1 : 0,
        modified_by: userId,
      };

      if ([1, 2, 3].includes(matchTypeId)) {
        term.event_category_id = parseInt(eventCatId, 10);
        term.event_type_id = parseInt(eventTypeId, 10);
        term.ttl = ttlMinutes * 60;
      }
      if (matchTypeId === 5) {
        term.replacement = replacement;
      }
      if (matchTypeId === 6) {
        term.area_id = parseInt(areaId, 10);
      }
      if (matchTypeId === 7) {
        term.device_id = parseInt(deviceId, 10);
        // Find device name for replacement field
        const dev = sortedDevices.find((d) => d.device_id === parseInt(deviceId, 10));
        term.replacement = dev?.device_name || '';
      }

      onEdit(term);
    } else {
      // Build add payload — array of match terms
      const base = {
        campus_id: campusId,
        palcare_device_id: serverId,
        pattern: pattern,
        replacement: '',
        match_type_id: 0,
        is_case_sensitive: caseSensitive ? 1 : 0,
        area_id: 0,
        device_id: 0,
        event_category_id: 0,
        event_type_id: 0,
        ttl: 0,
        modified_by: userId,
      };

      const terms = [];

      if (addTypeId === 1) {
        // Alarm Trigger — creates CREATE (1), REPEAT (2), CLEAR (3) together
        const alarmBase = {
          ...base,
          event_category_id: parseInt(eventCatId, 10),
          event_type_id: parseInt(eventTypeId, 10),
          ttl: ttlMinutes * 60,
        };
        terms.push({ ...alarmBase, match_type_id: 1 }); // Create
        terms.push({ ...alarmBase, match_type_id: 2 }); // Repeat
        terms.push({ ...alarmBase, match_type_id: 3 }); // Clear
      } else if (addTypeId === 2) {
        // Change Text
        terms.push({ ...base, match_type_id: 5, replacement });
      } else if (addTypeId === 3) {
        // Area Mapping
        const area = sortedAreas.find((a) => a.area_id === parseInt(areaId, 10));
        terms.push({ ...base, match_type_id: 6, area_id: parseInt(areaId, 10), replacement: area?.area_name || '' });
      } else if (addTypeId === 4) {
        // Device Mapping
        const dev = sortedDevices.find((d) => d.device_id === parseInt(deviceId, 10));
        terms.push({ ...base, match_type_id: 7, device_id: parseInt(deviceId, 10), replacement: dev?.device_name || '' });
      } else if (addTypeId === 5) {
        // Ignore
        terms.push({ ...base, match_type_id: 4 });
      }

      if (terms.length > 0) {
        onAdd(terms);
      }
    }
  }, [
    isEdit, mode, item, pattern, replacement, caseSensitive,
    eventCatId, eventTypeId, ttlMinutes, areaId, deviceId,
    addTypeId, matchTypeId, campusId, serverId, userId,
    onEdit, onAdd, sortedAreas, sortedDevices,
  ]);

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">
          {isEdit ? `Edit Match Term (${EDIT_TYPE_LABELS[matchTypeId] || ''})` : 'Add Match Term'}
        </h2>
        <button className="dp-drawer__close" onClick={onClose}>✕</button>
      </div>

      <form className="dp-drawer__body" onSubmit={handleSubmit}>
        {/* Type selector (add mode only) */}
        {!isEdit && (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Type</label>
            <select
              className="dp-drawer__select"
              value={addTypeId}
              onChange={(e) => setAddTypeId(parseInt(e.target.value, 10))}
            >
              {ADD_TYPE_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Pattern — always shown */}
        {showsPattern && (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Pattern</label>
            <input
              className="dp-drawer__input"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="Match pattern"
            />
          </div>
        )}

        {/* Replacement — Change type only */}
        {showsReplacement && (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">
              Replacement <span className="dp-drawer__required">*</span>
            </label>
            <input
              className="dp-drawer__input"
              value={replacement}
              onChange={(e) => setReplacement(e.target.value)}
              placeholder="Replacement text"
              required
            />
          </div>
        )}

        {/* Event Category + Event Type + TTL — Alarm types */}
        {showsAlarmFields && (
          <>
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">Event Category</label>
              <select
                className="dp-drawer__select"
                value={eventCatId}
                onChange={(e) => setEventCatId(e.target.value)}
              >
                {catOptions.map((cat) => (
                  <option key={cat.event_category_id} value={cat.event_category_id}>
                    {cat.event_category_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">Event Type</label>
              <select
                className="dp-drawer__select"
                value={eventTypeId}
                onChange={(e) => setEventTypeId(e.target.value)}
              >
                {typeOptions.map((t) => (
                  <option key={t.event_type_id} value={t.event_type_id}>
                    {t.event_type_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">TTL (minutes)</label>
              <input
                className="dp-drawer__input"
                type="number"
                min="0"
                value={ttlMinutes}
                onChange={(e) => setTtlMinutes(parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </>
        )}

        {/* Area selector */}
        {showsArea && (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Area</label>
            <select
              className="dp-drawer__select"
              value={areaId}
              onChange={(e) => setAreaId(e.target.value)}
            >
              <option value="">— Select Area —</option>
              {sortedAreas.map((area) => (
                <option key={area.area_id} value={area.area_id}>{area.area_name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Device selector */}
        {showsDevice && (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Device</label>
            <select
              className="dp-drawer__select"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
            >
              <option value="">— Select Device —</option>
              {sortedDevices.map((dev) => (
                <option key={dev.device_id} value={dev.device_id}>{dev.device_name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Case Sensitive */}
        <div className="dp-drawer__field dp-drawer__field--row">
          <input
            type="checkbox"
            className="dp-checkbox"
            id="case-sensitive"
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
          />
          <label htmlFor="case-sensitive" className="dp-drawer__label dp-drawer__label--inline">
            Case Sensitive
          </label>
        </div>

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Term')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MatchTermDrawer;
